import { useEffect, useState } from 'react';
import { mqttClient } from '@/shared/lib/mqtt/client';
import { useSession } from '@/features/auth/model/use-session';

/**
 * `live` — events are arriving. `connecting` — the client is trying. `off` — there is no broker.
 */
export type BrokerStatus = 'live' | 'connecting' | 'off';

/**
 * Whether the panel's live feed is actually up.
 *
 * ⚠️ **A control room cannot tell a quiet night from a dead connection.** Every figure on screen
 * comes from the API, so a broker that is down costs freshness rather than function — which is
 * exactly what makes it dangerous: nothing breaks, nothing errors, and the screen simply stops
 * changing while an operator keeps reading it.
 *
 * ⚠️ `off` is a legitimate state, not a failure: a development machine with no broker configured
 * must still run the whole panel (§5.5's client returns null there), and saying "off" is honest
 * where a red alarm would be noise.
 */
export function useBrokerStatus(): BrokerStatus {
  const { session } = useSession();
  const token = session?.accessToken;

  const [status, setStatus] = useState<BrokerStatus>('off');

  useEffect(() => {
    const client = mqttClient();

    if (!client) {
      setStatus('off');
      return;
    }

    // ⚠️ Seeded from the client's own state before any listener is attached. The connection is
    // shared and long-lived (§5.5), so by the time this component mounts the `connect` event has
    // usually already fired — a hook that only listened would report "connecting" forever.
    setStatus(client.connected ? 'live' : 'connecting');

    const live = () => setStatus('live');
    const trying = () => setStatus('connecting');

    client.on('connect', live);
    client.on('reconnect', trying);
    client.on('close', trying);
    client.on('offline', trying);

    return () => {
      client.off('connect', live);
      client.off('reconnect', trying);
      client.off('close', trying);
      client.off('offline', trying);
    };

    // Re-run on a renewed token: the shared client is replaced when the token changes (§12.1), and
    // listeners left on the old one would report the state of a connection nobody is using.
  }, [token]);

  return status;
}
